import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { ClearAggregationData, GetAggregationData } from '../../store/actions/aggregation.actions';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { Field } from '../../models/field.class';
import { getAggregationData$ } from '../../store/selectors/aggregation.selector';
import { Search } from '../../models/search.class';

@Component({
    selector: 'aggregations-menu',
    templateUrl: './aggregations-menu.component.html',
    styleUrls: ['./aggregations-menu.component.scss'],
    standalone: false,
})
export class AggregationsMenu implements OnInit, OnDestroy {
    @Input() column: string;
    @Input() entity: any;
    @Input() searchRecords: any;
    @Input() searchableIndexFields: any[];
    @Input() restApiName: string;
    @Input() serviceId: string;
    @Input() isFromClearance = false;
    @Input() reconCriteria: any;
    @Input() instanceId: string;
    @Input() clearAggregationData: BehaviorSubject<boolean>;
    @Input() appliedSearchCriteria: any;

    private unSubscribe = new Subject();
    private field: Field;
    public aggregationsData: { displayName: string; name: string; value: any }[] = [
        {
            displayName: 'Count',
            name: 'fieldValueCount',
            value: undefined,
        },
        {
            displayName: 'Sum',
            name: 'sumOfFieldValue',
            value: undefined,
        },
        {
            displayName: 'Average',
            name: 'averageValue',
            value: undefined,
        },
        {
            displayName: 'Maximum value',
            name: 'maxValue',
            value: undefined,
        },
        {
            displayName: 'Minimum value',
            name: 'minValue',
            value: undefined,
        },
        {
            displayName: 'Unique count',
            name: 'cardinalityFieldCount',
            value: undefined,
        },
    ];
    aggregationSubscription: Subscription;

    constructor(private store$: Store) {}

    public prepareAggregationsByDataType = () => {
        this.field = this.entity.fields?.find(
            field => field.uid === this.column || field.displayName === this.column || field.name === this.column
        );
        let validAggregations: string[] = [];
        switch (this.field.datatype) {
            case 'STRING':
            case 'BOOLEAN':
                validAggregations = ['fieldValueCount', 'cardinalityFieldCount'];
                break;
            case 'DATE':
                validAggregations = ['fieldValueCount', 'cardinalityFieldCount', 'maxValue', 'minValue'];
                break;
            default:
                validAggregations = ['fieldValueCount', 'sumOfFieldValue', 'averageValue', 'maxValue', 'minValue', 'cardinalityFieldCount'];
        }
        this.aggregationsData = this.aggregationsData.filter(item => validAggregations.includes(item.name));
    };

    public getAggregations = (aggregationType: string) => {
        let aggregations = {};
        const aggregationsObject = {
            fieldValueCount: {
                value_count: {
                    field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                },
            },
            sumOfFieldValue: {
                sum: {
                    field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                },
            },
            averageValue: {
                avg: {
                    field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                },
            },
            maxValue: {
                max: {
                    field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                },
            },
            minValue: {
                min: {
                    field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                },
            },
            cardinalityFieldCount: {
                cardinality_count: {
                    terms: [
                        {
                            field: `${this.field.searchableIndex}${this.field.datatype === 'STRING' ? '.raw' : ''}`,
                        },
                    ],
                },
            },
        };

        aggregations = aggregationType in aggregationsObject ? { [aggregationType]: aggregationsObject[aggregationType] } : {};
        this.getAggregationData(this.field, aggregations);
        this.setAggregationData();
    };

    private getAggregationData = (field, aggregations) => {
        let searchCriteria;
        if (!this.isFromClearance) {
            const compoundSearchCriteria = CommonUtilsService.cloneObject(this.appliedSearchCriteria);
            if (compoundSearchCriteria) {
                searchCriteria = Search.combineFieldAndCompoundSearchCriteria(
                    compoundSearchCriteria.query || compoundSearchCriteria,
                    this.searchRecords?.fields.filter(fld => fld?.value),
                    this.entity?.uid
                );
            } else {
                searchCriteria = Search.getRecordsSearchCriteria(
                    this.restApiName,
                    this.serviceId,
                    this.instanceId,
                    {
                        entityId: this.entity?.uid,
                        parentRecordId: this.entity.parentRecord?.id,
                    },
                    this.entity.pagination?.size,
                    this.searchRecords?.fields.filter(fld => fld?.value)
                );
            }
        } else {
            searchCriteria = CommonUtilsService.cloneObject(this.reconCriteria);
        }
        searchCriteria['aggs'] = aggregations;
        const data = {
            query: searchCriteria,
            searchEntityId: this.entity.uid,
            noAlerts: false,
        };
        this.store$.dispatch(
            GetAggregationData({
                instanceId: this.instanceId,
                parentRecordId: this.entity.parentRecord?.id || 'parentRecordId',
                fieldId: field?.searchableIndex,
                criteria: data,
            })
        );
    };

    public setAggregationData = () => {
        if (this.aggregationSubscription) return;
        this.aggregationSubscription = this.store$
            .select(getAggregationData$(this.instanceId, this.entity.parentRecord?.id || 'parentRecordId', this.field?.searchableIndex))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(response => {
                this.aggregationsData.forEach(item => (item.value = undefined));
                CommonUtilsService.cloneObject(response || [])?.forEach((aggregation: any) => {
                    const matchingAggregation = this.aggregationsData.find(agg => agg.name === aggregation.name);
                    if (matchingAggregation.name === 'minValue' || matchingAggregation.name === 'maxValue') {
                        if (this.field.datatype === 'DATE' && aggregation.value) {
                            aggregation.valueAsString = CommonUtilsService.transformDate(
                                aggregation.valueAsString as any,
                                'DD-MM-YYYY' as any,
                                this.field.outputFormat as any
                            );
                        }
                    }
                    if (matchingAggregation) {
                        const value = aggregation?.valueAsString ?? aggregation?.value ?? aggregation?.valueCount;
                        matchingAggregation.value = value === 'Infinity' || value === '-Infinity' ? 'N/A' : value;
                    }
                });
            });
    };

    ngOnInit(): void {
        this.clearAggregationData
            ?.pipe(
                takeUntil(this.unSubscribe),
                filter(response => !!response)
            )
            .subscribe(() => {
                this.store$.dispatch(
                    ClearAggregationData({ instanceId: this.instanceId, parentRecordId: this.entity.parentRecord?.id || 'parentRecordId' })
                );
            });
    }

    ngOnDestroy() {
        this.unSubscribe.next(undefined);
        this.unSubscribe.complete();
        this.aggregationSubscription?.unsubscribe();
    }
}
